import axios from 'axios';
import { SeriesOptionsType } from 'highcharts/highstock';
import {
    DateInterval,
    MonthHourHeatMapData,
    QuickPickSeries,
    ReportItem,
    ReportItemTypeInfo,
    SeriesTimePoint,
    StandardReportInfo,
    StandardReportType,
    StatId,
    VariableId,
} from 'types/reporting';

const REPORTING_BASE_ROUTE = '/reporting2';

export const getReportTypeLabel = (reportType: StandardReportType): string => {
    switch (reportType) {
        case 'PortfolioEnergyAndRev':
            return 'Portfolio Energy and Revenue';
        default:
            return reportType;
    }
};

export const getReportTypes = async (studyId: number): Promise<StandardReportType[]> => {
    return ['PortfolioEnergyAndRev'];
};

export const getReportInfo = async (studyId: number, reportType: StandardReportType): Promise<StandardReportInfo> => {
    const request = await axios.get<StandardReportInfo>(`${REPORTING_BASE_ROUTE}/info/${studyId}/${reportType}`);
    return request.data;
};

export interface GerTimeSeriesDataParams {
    reportType?: StandardReportType;
    studyId?: number;
    portfolioId?: number;
    itemTypeId?: number[];
    id?: number;
    dateIntervalId?: DateInterval[];
    quickPickSeries: QuickPickSeries;
    variableId?: VariableId;
    statId?: StatId;
}

export const getTimeSeriesData = async (
    studyId: number,
    reportType: StandardReportType,
    params: GerTimeSeriesDataParams
): Promise<SeriesTimePoint[]> => {
    const data = await axios.get<SeriesTimePoint[]>(`${REPORTING_BASE_ROUTE}/timeseries/${studyId}/${reportType}`, { params });
    return data.data;
};

export const getReportItemData = async (
    item: ReportItem,
    studyId: number,
    reportType: StandardReportType,
    params: GerTimeSeriesDataParams
): Promise<{ item: ReportItem; data: SeriesTimePoint[] }> => {
    const data = await getTimeSeriesData(studyId, reportType, params);
    return {
        item,
        data,
    };
};

export const getSeriesFromReportChartSpec = async (
    specs: ReportItemTypeInfo,
    studyId: number,
    itemId: number,
    reportType: StandardReportType,
    dateInterval: DateInterval
): Promise<SeriesOptionsType[]> => {
    const series: Highcharts.SeriesOptionsType[] = [];
    for (const spec of specs.seriesSpecs) {
        const dateIntervalAbbr = dateInterval;
        // eslint-disable-next-line no-new-func
        const quickPickSeries = new Function('dateIntervalAbbr', `return \`${spec.quickPickExp}\`;`)(dateIntervalAbbr);
        series.push({
            ...spec,
            type: (spec.type as any) || 'line',
            name: spec.description,
            data: await getTimeSeriesData(studyId, reportType, {
                id: itemId,
                quickPickSeries: quickPickSeries as QuickPickSeries,
            }),
        });
    }

    return series;
};

export const getNetPositionHeatMapData = async (
    studyId: number,
    reportType: StandardReportType,
    itemId: number
): Promise<MonthHourHeatMapData> => {
    const res = await axios.get<MonthHourHeatMapData>(
        `${REPORTING_BASE_ROUTE}/netpositionheatmap/${studyId}/${reportType}/${itemId}`
    );
    return res.data;
};
